import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    helpIcon: {
        fontSize: '16px',
        color: theme.palette.text.secondary,
        cursor: 'help',
    },
}));

function TestAgentsCellDetail({ agents, threshold }) {
    const classes = useStyles();

    const { agentText, agentHelpText } = parseAgents(agents, threshold);

    if (!agentHelpText) {
        return <span>{agentText}</span>;
    }

    return (
        <div className={classes.container}>
            <span>{agentText}</span>
            <Tooltip title={agentHelpText} placement="top">
                <Icon className={classes.helpIcon} aria-label="View agent details">
                    help_outline
                </Icon>
            </Tooltip>
        </div>
    );
}

TestAgentsCellDetail.propTypes = {
    agents: PropTypes.shape({
        all: PropTypes.bool,
        populated: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
            })
        ),
        tags: PropTypes.arrayOf(PropTypes.string),
    }),
    threshold: PropTypes.number,
};

TestAgentsCellDetail.defaultProps = {
    agents: null,
    threshold: 0,
};

/**
 * Displays test agents with conditional tooltip
 *
 * @param {Object} props
 * @param {Object} props.agents - Agents object from test data
 *
 * @example
 * // All agents
 * <TestAgentsCell agents={{ all: true }} />
 * // Result: "All nPoints" (no tooltip)
 *
 * @example
 * // 2+ selected agents (threshold = 0, so > 0 shows count + tooltip)
 * <TestAgentsCell agents={{ populated: [{name: 'Agent1'}, {name: 'Agent2'}] }} />
 * // Result: "Selected 2 nPoints" + tooltip with names
 *
 * @example
 * // 1 selected agent (threshold = 0, so any count shows tooltip)
 * <TestAgentsCell agents={{ populated: [{name: 'Agent1'}] }} />
 * // Result: "Selected 1 nPoints" + tooltip with name
 *
 * @example
 * // Tagged agents
 * <TestAgentsCell agents={{ tags: ['Production', 'US-East'] }} />
 * // Result: "Tagged nPoints" + tooltip with tag names
 */
export function TestAgentsCell({ agents, threshold = 0 }) {
    if (isEmpty(agents)) {
        return <span>--</span>;
    }

    return <TestAgentsCellDetail agents={agents} threshold={threshold} />;
}

TestAgentsCell.propTypes = {
    agents: PropTypes.shape({
        all: PropTypes.bool,
        populated: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
            })
        ),
        tags: PropTypes.arrayOf(PropTypes.string),
    }),
    threshold: PropTypes.number,
};

TestAgentsCell.defaultProps = {
    agents: null,
    threshold: 0,
};

/**
 * Parse agents object into display text and optional tooltip.
 *
 * @param {Object} agents - Agent selection object
 * @param {number} [threshold=0] - When populated count is at or below threshold,
 *   show agent names directly (no "Selected N" prefix, no tooltip).
 *   portal/ui default is 1 for VoIP Caller/Callee columns.
 */
export function parseAgents(agents, threshold = 0) {
    if (agents.all) {
        return { agentText: 'All nPoints', agentHelpText: undefined };
    }

    const agentsPopulated = agents.populated || [];
    const populated = agentsPopulated.filter((a) => a != null);

    const agentsTags = agents.tags || [];
    const tags = agentsTags.filter((a) => a != null);

    if (populated.length > 0) {
        const agentNames = populated
            .map((a) => a.name || 'N/A')
            .filter(Boolean)
            .join(', ');

        if (populated.length > threshold) {
            return {
                agentText: `Selected ${populated.length} nPoints`,
                agentHelpText: agentNames,
            };
        }

        // At or below threshold — show names directly (matches portal/ui TestAgentsParser)
        return { agentText: agentNames, agentHelpText: undefined };
    }

    if (tags.length > 0) {
        const tagNames = tags.filter(Boolean).join(', ');
        return {
            agentText: 'Tagged nPoints',
            agentHelpText: tagNames,
        };
    }

    return { agentText: '--', agentHelpText: undefined };
}
